<script setup lang="ts">
import {Checkbox} from "@/core/components/ui/checkbox";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import GiftStatusBadge from "@/birthday-gift/components/GiftStatusBadge.vue";
import formatHelper from "@/core/helpers/formatHelper";
import {GIFT_PALETTES} from "@/birthday-gift/constants/palettes";
import {BirthdayGift, GiftSendHour, TodayBirthdayVoucher} from "@/birthday-gift/interfaces";

defineProps<{
    vouchers: TodayBirthdayVoucher[];
    gifts: BirthdayGift[];
    sendHour: GiftSendHour;
    /** Ids of the vouchers whose row is awaiting the server. */
    pendingIds: number[];
}>();

const emit = defineEmits<{
    (e: 'toggle-exclusion', voucher: TodayBirthdayVoucher, excluded: boolean): void;
    (e: 'override-gift', voucher: TodayBirthdayVoucher, giftId: number): void;
}>();

const initials = (voucher: TodayBirthdayVoucher) =>
    `${voucher.customer.first_name.charAt(0)}${voucher.customer.last_name.charAt(0)}`.toUpperCase();

const paletteOf = (giftId: number | undefined, gifts: BirthdayGift[]) => {
    const gift = gifts.find((item) => item.id === giftId);

    return gift ? GIFT_PALETTES[gift.palette] : null;
};

/** A voucher that already went out can no longer be changed. */
const isLocked = (voucher: TodayBirthdayVoucher) => ['sent', 'redeemed', 'expired', 'failed']
    .includes(voucher.status);
</script>

<template>
  <div class="divide-y divide-gray-light">
    <div
      v-for="voucher in vouchers"
      :key="voucher.id"
      class="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3"
      :class="{
        'bg-gray-light/60': voucher.status === 'excluded',
        'opacity-70': voucher.status === 'blocked'
      }"
    >
      <Checkbox
        :model-value="voucher.status !== 'excluded'"
        :disabled="isLocked(voucher) || pendingIds.includes(voucher.id)"
        :aria-label="`Inclure ${voucher.customer.first_name} ${voucher.customer.last_name}`"
        @update:model-value="emit('toggle-exclusion', voucher, !$event)"
      />

      <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-lightPurple-lightest text-xs font-semibold text-primary">
        {{ initials(voucher) }}
      </span>

      <div class="min-w-[150px] flex-1 basis-40">
        <div class="text-sm font-semibold">
          {{ voucher.customer.first_name }} {{ voucher.customer.last_name }}
        </div>
        <div class="text-xs text-muted-foreground">
          {{ voucher.customer.age }} ans · {{ voucher.customer.sex === 'f' ? 'femme' : 'homme' }}
          <template v-if="voucher.customer.phone">
            · {{ formatHelper.readablePhone(voucher.customer.phone) }}
          </template>
        </div>
      </div>

      <div class="flex min-w-[220px] flex-1 basis-60 items-center gap-2">
        <span
          v-if="paletteOf(voucher.gift?.id, gifts)"
          class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
          :style="{background: paletteOf(voucher.gift?.id, gifts)?.tint}"
        >
          <component
            :is="paletteOf(voucher.gift?.id, gifts)?.icon"
            class="h-3.5 w-3.5"
            :style="{color: paletteOf(voucher.gift?.id, gifts)?.iconColor}"
          />
        </span>
        <Select
          :model-value="voucher.gift ? String(voucher.gift.id) : undefined"
          :disabled="isLocked(voucher) || pendingIds.includes(voucher.id)"
          @update:model-value="emit('override-gift', voucher, Number($event))"
        >
          <SelectTrigger class="w-full">
            <SelectValue placeholder="Aucun cadeau" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="gift in gifts"
              :key="gift.id"
              :value="String(gift.id)"
            >
              {{ gift.name }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div class="ml-auto flex items-center gap-3">
        <span class="whitespace-nowrap text-xs text-muted-foreground">
          {{ voucher.gift_overridden ? 'choix manuel' : 'profil' }}
        </span>
        <GiftStatusBadge
          :status="voucher.status"
          :blocked-reason="voucher.blocked_reason"
          :send-hour="sendHour"
          :sent-at="voucher.sent_at"
        />
      </div>
    </div>
  </div>
</template>
